import { DateTime } from 'luxon';

/**
 * Converts a date string or timestamp in milliseconds to milliseconds at midnight in the given time zone.
 * @param {string|number} dateStringOrMS - The date string in the format YYYY-MM-DD or a timestamp in milliseconds.
 * @param {string} timeZone - The time zone to convert the date to.
 * @throws {Error} - If no valid time zone is given or if the date is not in the correct format.
 * @returns {number} The milliseconds at midnight in the given time zone.
 */
export function dateToMsInTimeZone(dateStringOrMS, timeZone) {
  if (!TIME_ZONES.includes(timeZone)) throw Error('Invalid time zone given');
  const dateIsValidNumber = dateStringOrMS != 0 && isNaN(Number(dateStringOrMS)) == false;
  const dateIsString = typeof dateStringOrMS === 'string' && dateStringOrMS.match(/^\d{4}-\d{2}-\d{2}$/);
  if (!dateIsValidNumber && !dateIsString) {
    throw Error('Date must either be a string in the format YYYY-MM-DD or a timestamp in milliseconds');
  }
  const date = dateIsString
    ? DateTime.fromISO(dateStringOrMS, { zone: timeZone })
    : DateTime.fromMillis(Number(dateStringOrMS), { zone: timeZone });
  const midnight = date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  return midnight.toMillis();
}

/**
 * Converts a timestamp in milliseconds to a DateTime object at midnight in the given time zone.
 * @param {number} timestampInMS - The timestamp in milliseconds.
 * @param {string} timeZone - The time zone to convert the date to.
 * @throws {Error} - If no valid time zone is given or if the timestamp is not a number in milliseconds.
 * @returns {DateTime} The DateTime object representing the converted timestamp.
 */
export function msToDateTimeInTimeZone(timestampInMS, timeZone) {
  if (!TIME_ZONES.includes(timeZone)) throw Error('Invalid time zone given');
  if (typeof timestampInMS !== 'number') throw Error('Timestamp must be a number in milliseconds');
  const date = DateTime.fromMillis(timestampInMS, { zone: timeZone });
  const midnight = date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  return midnight;
}

export const TIME_ZONES = [
  'America/Anchorage',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/New_York',
  'America/Phoenix',
  'America/Puerto_Rico',
  'Pacific/Honolulu'
];
